// Payments/BalanceDisplay.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from '../../../utils/permissionUtils';

import {
  Box, Typography, Button, CircularProgress, Modal, Alert, TextField,
  IconButton, Grid, Card, CardContent, Chip, List, ListItem, ListItemText,
  Divider, Paper, Stack, alpha, useTheme, Radio, RadioGroup,
  FormControlLabel, Tooltip
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
const FONT = "'Plus Jakarta Sans', sans-serif";
const MONO = '"JetBrains Mono", monospace';

// Shared modal paper style
const modalPaperSx = (theme) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 480 },
    maxWidth: 480,
    p: 0,
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0 20px 60px ${alpha('#000', 0.12)}`,
    overflow: 'hidden',
    maxHeight: '90vh',
    overflowY: 'auto',
});

const BalanceDisplay = ({ onWithdrawalSuccess, refreshKey }) => {
    const theme = useTheme();
    const [balance, setBalance] = useState(null);
    const [cryptoBalance, setCryptoBalance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAccountReady, setIsAccountReady] = useState(false);
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Modals
    const [connectFiatOpen, setConnectFiatOpen] = useState(false);
    const [connectCryptoOpen, setConnectCryptoOpen] = useState(false);
    const [withdrawOpen, setWithdrawOpen] = useState(false);

    // Crypto wallet connection
    const [savedWalletAddress, setSavedWalletAddress] = useState('');
    const [manualWalletInput, setManualWalletInput] = useState('');
    const [cryptoConnecting, setCryptoConnecting] = useState(false);
    const [cryptoSaveError, setCryptoSaveError] = useState('');

    // Withdrawal fields
    const [withdrawalAmount, setWithdrawalAmount] = useState('');
    const [withdrawMethod, setWithdrawMethod] = useState('stripe'); // stripe | paypal | crypto
    const [paypalEmail, setPaypalEmail] = useState('');
    const [cryptoWalletAddress, setCryptoWalletAddress] = useState('');

    const [planRatios, setPlanRatios] = useState({
        all_plan_ratios: [],
        current_user_plan: null
    });
    const [isRatiosLoading, setIsRatiosLoading] = useState(true);

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);


    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('payments', 'view');
                const modifyPerm = await hasPermission('payments', 'add|edit');
                setCanView(viewPerm);
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        setIsLoading(true);
        axiosInstance.get(`/payments/balance`)
            .then(res => {
                setBalance(res.data.balance);
                setCryptoBalance(res.data.crypto_balance);
                setIsAccountReady(res.data.is_stripe_account_ready);
            })
            .catch(err => setError('Failed to load balance.'))
            .finally(() => setIsLoading(false));

        setIsRatiosLoading(true);
        axiosInstance.get(`/payments/plan-ratios`)
            .then(res => {
                setPlanRatios(res.data.data);
            })
            .catch(err => { if (!axiosInstance.isCancel(err)) console.error("Failed to fetch plan ratios", err); })
            .finally(() => setIsRatiosLoading(false));

        // Fetch saved crypto wallet
        axiosInstance.get(`/pay/config`)
            .then(res => {
                if (res.data?.data?.wallet_address) {
                    setSavedWalletAddress(res.data.data.wallet_address);
                }
            })
            .catch(() => {});

    }, [refreshKey]);

    // ── Handlers ──

    const handleConnectStripe = async () => {
        setIsProcessing(true);
        try {
            const res = await axiosInstance.post(`/payments/create-connect-link`, {
                return_url: window.location.href,
                refresh_url: window.location.href,
            });
            window.location.href = res.data.url;
        } catch (err) {
            setError(err.response?.data?.message || 'Could not create Stripe connection link.');
            setIsProcessing(false);
        }
    };

    const handleWithdraw = async () => {
        setIsProcessing(true);
        setError('');
        try {
            if (withdrawMethod === 'crypto') {
                await axiosInstance.post(`/payments/initiate-crypto-withdrawal`, {
                    amount: withdrawalAmount,
                    wallet_address: cryptoWalletAddress,
                });
            } else {
                const payload = {
                    amount: withdrawalAmount,
                    method: withdrawMethod,
                };
                if (withdrawMethod === 'paypal') {
                    payload.paypal_email = paypalEmail;
                }
                await axiosInstance.post(`/payments/initiate-withdrawal`, payload);
            }
            setWithdrawOpen(false);
            onWithdrawalSuccess();
        } catch (err) {
            setError(err.response?.data?.message || 'Withdrawal failed.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleOpenConnectCrypto = () => {
        setManualWalletInput(savedWalletAddress || '');
        setCryptoSaveError('');
        setConnectCryptoOpen(true);
    };

    const handleSaveManualWallet = async () => {
        const wallet = manualWalletInput.trim();
        if (!wallet) return;
        if (!/^(0x[a-fA-F0-9]{40}|[1-9A-HJ-NP-Za-km-z]{32,44})$/.test(wallet)) {
            setCryptoSaveError('Invalid wallet address format. Enter a valid EVM (0x...) or Solana address.');
            return;
        }
        setCryptoConnecting(true);
        setCryptoSaveError('');
        try {
            await axiosInstance.patch(`/pay/config`, { wallet_address: wallet });
            setSavedWalletAddress(wallet);
            setConnectCryptoOpen(false);
        } catch (err) {
            setCryptoSaveError(err.response?.data?.message || 'Failed to save wallet address.');
        } finally {
            setCryptoConnecting(false);
        }
    };

    const handleConnectCoinbaseWallet = async () => {
        setCryptoConnecting(true);
        setCryptoSaveError('');
        try {
            const { CoinbaseWalletSDK } = await import('@coinbase/wallet-sdk');
            const sdk = new CoinbaseWalletSDK({ appName: 'Klonit', appChainIds: [8453] });
            const provider = sdk.makeWeb3Provider({ options: 'smartWalletOnly' });
            const accounts = await provider.request({ method: 'eth_requestAccounts' });
            const address = accounts[0];
            if (address) {
                await axiosInstance.patch(`/pay/config`, {
                    wallet_address: address,
                    wallet_type: 'coinbase_wallet',
                });
                setSavedWalletAddress(address);
                setConnectCryptoOpen(false);
            }
        } catch (err) {
            setCryptoSaveError(err.message || 'Failed to connect Coinbase Wallet.');
        } finally {
            setCryptoConnecting(false);
        }
    };

    const handleDisconnectCryptoWallet = async () => {
        setCryptoConnecting(true);
        setCryptoSaveError('');
        try {
            await axiosInstance.patch(`/pay/config`, { wallet_address: '' });
            setSavedWalletAddress('');
            setConnectCryptoOpen(false);
        } catch (err) {
            setCryptoSaveError(err.response?.data?.message || 'Failed to disconnect wallet.');
        } finally {
            setCryptoConnecting(false);
        }
    };

    const handleOpenWithdraw = () => {
        const hasFiat = balance && parseFloat(balance) > 0;
        const hasCrypto = cryptoBalance && parseFloat(cryptoBalance) > 0;
        const defaultMethod = !hasFiat && hasCrypto ? 'crypto' : 'stripe';
        setWithdrawMethod(defaultMethod);
        setWithdrawalAmount(defaultMethod === 'crypto' ? (cryptoBalance || '0') : (balance || '0'));
        setPaypalEmail('');
        setCryptoWalletAddress(savedWalletAddress || '');
        setError('');
        setWithdrawOpen(true);
    };

    const handleOpenConnectFiat = () => {
        setError('');
        setConnectFiatOpen(true);
    };


    // ── Guards ──

    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography sx={{ fontFamily: FONT }}>Checking permissions...</Typography>
            </Box>
        );
    }

    if (!canView) {
        return (
            <Alert severity="error" sx={{ mt: 6, fontFamily: FONT }}>
                <strong>Access Denied</strong>
                <br />
                You don't have permission to view Payment settings. Please contact your administrator.
            </Alert>
        );
    }

    if (isLoading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
        </Box>
    );

    // ── Derived ──

    const activeBalance = withdrawMethod === 'crypto' ? cryptoBalance : balance;
    const isWithdrawalAmountInvalid = !withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || parseFloat(withdrawalAmount) > parseFloat(activeBalance);
    const isCryptoFormInvalid = withdrawMethod === 'crypto' && !cryptoWalletAddress.trim();
    const isPaypalEmailInvalid = withdrawMethod === 'paypal' && !paypalEmail.trim();

    const hasBalance = balance && parseFloat(balance) > 0;
    const hasCryptoBalance = cryptoBalance && parseFloat(cryptoBalance) > 0;
    const cardSx = {
        elevation: 0,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: 'none',
    };

    const btnSx = {
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 600,
        fontFamily: FONT,
    };

    return (
        <Box sx={{ fontFamily: FONT }}>
            {/* ── Balance Cards ── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                {/* Fiat Balance */}
                <Card sx={{
                    ...cardSx,
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    flex: 1,
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                            <Box sx={{
                                width: 40, height: 40, borderRadius: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            }}>
                                <AccountBalanceWalletIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, fontFamily: FONT, color: theme.palette.text.secondary, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}
                            >
                                Fiat Balance
                            </Typography>
                        </Stack>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700, fontFamily: MONO,
                                fontFeatureSettings: '"tnum"',
                                color: theme.palette.primary.main, lineHeight: 1.1,
                            }}
                        >
                            {balance ? `$${Number(balance).toFixed(2)}` : '$0.00'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontFamily: FONT, mt: 0.5, display: 'block' }}>
                            Withdraw via Stripe or PayPal
                        </Typography>
                    </CardContent>
                </Card>

                {/* Crypto Balance */}
                <Card sx={{
                    ...cardSx,
                    borderLeft: `4px solid #2775ca`,
                    flex: 1,
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                            <Box sx={{
                                width: 40, height: 40, borderRadius: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha('#2775ca', 0.08),
                            }}>
                                <CurrencyBitcoinIcon sx={{ color: '#2775ca', fontSize: 22 }} />
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, fontFamily: FONT, color: theme.palette.text.secondary, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}
                            >
                                Crypto Balance (USDC)
                            </Typography>
                        </Stack>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700, fontFamily: MONO,
                                fontFeatureSettings: '"tnum"',
                                color: '#2775ca', lineHeight: 1.1,
                            }}
                        >
                            {cryptoBalance ? `${Number(cryptoBalance).toFixed(2)}` : '0.00'}
                            <Typography component="span" sx={{ fontFamily: FONT, fontSize: '0.9rem', fontWeight: 600, color: '#2775ca', ml: 1 }}>
                                USDC
                            </Typography>
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontFamily: FONT, mt: 0.5, display: 'block' }}>
                            Withdraw USDC to any supported network
                        </Typography>
                    </CardContent>
                </Card>
            </Stack>

            {/* ── Withdraw & Connect Section ── */}
            <Card sx={{ ...cardSx, mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AccountBalanceWalletIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                Withdraw
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            {isAccountReady && (
                                <Chip
                                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                    label="Stripe"
                                    size="small"
                                    sx={{ bgcolor: alpha('#10b981', 0.12), color: '#10b981', fontWeight: 600, '& .MuiChip-icon': { color: '#10b981' } }}
                                />
                            )}
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="caption" sx={{ fontFamily: FONT, color: theme.palette.text.secondary, fontWeight: 500 }}>Fiat</Typography>
                            <Typography variant="h5" sx={{ fontFamily: MONO, fontWeight: 700, color: theme.palette.primary.main, fontFeatureSettings: '"tnum"' }}>
                                ${balance ? Number(balance).toFixed(2) : '0.00'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ fontFamily: FONT, color: theme.palette.text.secondary, fontWeight: 500 }}>Crypto</Typography>
                            <Typography variant="h5" sx={{ fontFamily: MONO, fontWeight: 700, color: '#2775ca', fontFeatureSettings: '"tnum"' }}>
                                {cryptoBalance ? Number(cryptoBalance).toFixed(2) : '0.00'} <Typography component="span" sx={{ fontSize: '0.7rem', fontFamily: FONT, fontWeight: 600 }}>USDC</Typography>
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        {!isAccountReady && canModify && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpenConnectFiat}
                                startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
                                sx={{ ...btnSx, fontSize: '0.8rem', borderColor: alpha(theme.palette.primary.main, 0.4), color: theme.palette.primary.main }}
                            >
                                Connect Stripe
                            </Button>
                        )}
                        {canModify && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpenConnectCrypto}
                                startIcon={savedWalletAddress
                                    ? <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                    : <CurrencyBitcoinIcon sx={{ fontSize: 16 }} />
                                }
                                sx={{
                                    ...btnSx, fontSize: '0.8rem',
                                    borderColor: savedWalletAddress ? alpha('#10b981', 0.4) : alpha('#2775ca', 0.4),
                                    color: savedWalletAddress ? '#10b981' : '#2775ca',
                                }}
                            >
                                {savedWalletAddress ? 'Crypto Wallet Connected' : 'Connect Crypto Wallet'}
                            </Button>
                        )}
                    </Stack>

                    {canModify && (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleOpenWithdraw}
                            disabled={!hasBalance && !hasCryptoBalance}
                            startIcon={<AccountBalanceWalletIcon sx={{ fontSize: 18 }} />}
                            sx={{ ...btnSx, py: 1.2, mt: 1 }}
                        >
                            Withdraw
                        </Button>
                    )}
                </CardContent>
            </Card>

            {/* ── Error Alert ── */}
            {error && !withdrawOpen && !connectFiatOpen && !connectCryptoOpen && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontFamily: FONT }}>{error}</Alert>
            )}

            {/* ── Plan Ratio Section ── */}
            <Grid container spacing={2.5}>
                {/* Current Plan Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ ...cardSx, height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                                <TrendingUpIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                    Your Current Plan
                                </Typography>
                            </Stack>
                            {isRatiosLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                planRatios.current_user_plan ? (
                                    <Box>
                                        <Chip
                                            label={planRatios.current_user_plan.plan_name}
                                            size="small"
                                            sx={{
                                                mb: 2, fontWeight: 600, fontFamily: FONT,
                                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                color: theme.palette.primary.main,
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontFamily: FONT, mb: 0.5 }}>
                                            Your revenue share per payment
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 700, fontFamily: MONO,
                                                fontFeatureSettings: '"tnum"',
                                                color: theme.palette.success.dark,
                                            }}
                                        >
                                            {planRatios.current_user_plan.user_share}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2, fontFamily: FONT }}>
                                        No active plan found.
                                    </Alert>
                                )
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* All Plans Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ ...cardSx, height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                                <InfoOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                    Revenue Share by Plan
                                </Typography>
                            </Stack>
                            {isRatiosLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <List dense disablePadding>
                                    {planRatios.all_plan_ratios.map((plan, index) => (
                                        <React.Fragment key={plan.plan_name}>
                                            <ListItem sx={{ px: 0, py: 1 }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: FONT, color: theme.palette.text.primary }}>
                                                            {plan.plan_name}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="caption" sx={{ fontFamily: FONT, color: theme.palette.text.secondary }}>
                                                            User receives{' '}
                                                            <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, fontFeatureSettings: '"tnum"', color: theme.palette.text.primary }}>
                                                                {plan.user_share}
                                                            </Box>
                                                            {' '}of each payment
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                            {index < planRatios.all_plan_ratios.length - 1 && (
                                                <Divider component="li" sx={{ borderColor: alpha(theme.palette.divider, 0.5) }} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ════════════════════════════════════════════════════════════════
                MODAL 1: Connect Fiat (Stripe / PayPal)
            ════════════════════════════════════════════════════════════════ */}
            <Modal open={connectFiatOpen} onClose={() => setConnectFiatOpen(false)}>
                <Paper sx={modalPaperSx(theme)}>
                    {/* Header */}
                    <Box sx={{
                        px: 3, py: 2.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                    }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            }}>
                                <LinkIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                Connect Fiat Withdrawal
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Body */}
                    <Box sx={{ px: 3, py: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT, mb: 3 }}>
                            Connect your Stripe or PayPal account to receive fiat withdrawals.
                        </Typography>

                        {/* Stripe */}
                        <Paper variant="outlined" sx={{
                            p: 2.5, mb: 2, borderRadius: 2,
                            border: `1px solid ${alpha('#635bff', 0.2)}`,
                            bgcolor: alpha('#635bff', 0.02),
                        }}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <CreditCardIcon sx={{ color: '#635bff', fontSize: 24 }} />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                                Stripe Connect
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                                Receive payouts to your bank account
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    {isAccountReady && (
                                        <Chip
                                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                            label="Connected"
                                            size="small"
                                            sx={{ bgcolor: alpha('#10b981', 0.12), color: '#10b981', fontWeight: 600, '& .MuiChip-icon': { color: '#10b981' } }}
                                        />
                                    )}
                                </Stack>
                                <Button
                                    variant={isAccountReady ? "outlined" : "contained"}
                                    fullWidth
                                    onClick={handleConnectStripe}
                                    disabled={isProcessing}
                                    startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        ...btnSx,
                                        ...(isAccountReady ? {
                                            borderColor: alpha('#635bff', 0.3),
                                            color: '#635bff',
                                        } : {
                                            bgcolor: '#635bff',
                                            '&:hover': { bgcolor: alpha('#635bff', 0.88) },
                                        }),
                                    }}
                                >
                                    {isAccountReady ? 'Manage Stripe Account' : 'Connect Stripe Account'}
                                </Button>
                            </Stack>
                        </Paper>

                        {/* PayPal */}
                        <Paper variant="outlined" sx={{
                            p: 2.5, borderRadius: 2,
                            border: `1px solid ${alpha('#003087', 0.2)}`,
                            bgcolor: alpha('#003087', 0.02),
                        }}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <PaymentIcon sx={{ color: '#003087', fontSize: 24 }} />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                            PayPal
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                            Withdraw directly to your PayPal account
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Alert severity="info" sx={{ borderRadius: 2, fontFamily: FONT, py: 0.5 }}>
                                    PayPal withdrawals are available at checkout. No separate connection required.
                                </Alert>
                            </Stack>
                        </Paper>

                        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontFamily: FONT }}>{error}</Alert>}
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        px: 3, py: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                    }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setConnectFiatOpen(false)}
                            sx={{ ...btnSx, borderColor: theme.palette.divider, color: theme.palette.text.secondary }}
                        >
                            Close
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* ════════════════════════════════════════════════════════════════
                MODAL 2: Connect Crypto Wallet
            ════════════════════════════════════════════════════════════════ */}
            <Modal open={connectCryptoOpen} onClose={() => setConnectCryptoOpen(false)}>
                <Paper sx={modalPaperSx(theme)}>
                    {/* Header */}
                    <Box sx={{
                        px: 3, py: 2.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha('#2775ca', 0.03),
                    }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha('#2775ca', 0.08),
                            }}>
                                <CurrencyBitcoinIcon sx={{ color: '#2775ca', fontSize: 20 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                Connect Crypto Wallet
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Body */}
                    <Box sx={{ px: 3, py: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: FONT, mb: 3 }}>
                            Add your USDC wallet address for crypto withdrawals on Base network.
                        </Typography>

                        {/* Current wallet display */}
                        {savedWalletAddress && (
                            <Paper variant="outlined" sx={{
                                p: 2, mb: 2.5, borderRadius: 2,
                                border: `1px solid ${alpha('#10b981', 0.3)}`,
                                bgcolor: alpha('#10b981', 0.04),
                            }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: FONT, color: '#10b981' }}>
                                        Connected Wallet
                                    </Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: theme.palette.text.primary }}>
                                    {savedWalletAddress}
                                </Typography>
                            </Paper>
                        )}

                        {/* Manual Entry */}
                        <Paper variant="outlined" sx={{
                            p: 2.5, mb: 2, borderRadius: 2,
                            border: `1px solid ${alpha('#2775ca', 0.2)}`,
                            bgcolor: alpha('#2775ca', 0.02),
                        }}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <AccountBalanceWalletIcon sx={{ color: '#2775ca', fontSize: 24 }} />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                            Enter Wallet Address
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                            Paste your USDC wallet address (Base network)
                                        </Typography>
                                    </Box>
                                </Stack>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="0x... your wallet address"
                                    value={manualWalletInput}
                                    onChange={(e) => setManualWalletInput(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace', fontSize: '0.85rem' },
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleSaveManualWallet}
                                    disabled={cryptoConnecting || !manualWalletInput.trim()}
                                    startIcon={cryptoConnecting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    sx={{ ...btnSx, bgcolor: '#2775ca', '&:hover': { bgcolor: alpha('#2775ca', 0.88) } }}
                                >
                                    {savedWalletAddress ? 'Update Wallet' : 'Save Wallet'}
                                </Button>
                            </Stack>
                        </Paper>

                        {/* Coinbase Wallet Connect */}
                        <Paper variant="outlined" sx={{
                            p: 2.5, borderRadius: 2,
                            border: `1px solid ${alpha('#0052ff', 0.2)}`,
                            bgcolor: alpha('#0052ff', 0.02),
                        }}>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box sx={{
                                        width: 24, height: 24, borderRadius: 1,
                                        bgcolor: '#0052ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem' }}>CB</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                            Coinbase Wallet
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                            Connect your Coinbase Smart Wallet on Base
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleConnectCoinbaseWallet}
                                    disabled={cryptoConnecting}
                                    startIcon={cryptoConnecting ? <CircularProgress size={16} color="inherit" /> : <LinkIcon sx={{ fontSize: 16 }} />}
                                    sx={{ ...btnSx, borderColor: alpha('#0052ff', 0.3), color: '#0052ff' }}
                                >
                                    Connect with Coinbase Wallet
                                </Button>
                            </Stack>
                        </Paper>

                        {cryptoSaveError && <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontFamily: FONT }}>{cryptoSaveError}</Alert>}
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        px: 3, py: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                    }}>
                        <Stack direction="row" spacing={1.5}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setConnectCryptoOpen(false)}
                                sx={{ ...btnSx, borderColor: theme.palette.divider, color: theme.palette.text.secondary }}
                            >
                                Close
                            </Button>
                            {savedWalletAddress && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleDisconnectCryptoWallet}
                                    disabled={cryptoConnecting}
                                    sx={{ ...btnSx, borderColor: alpha('#ef4444', 0.3), color: '#ef4444' }}
                                >
                                    Disconnect
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Paper>
            </Modal>

            {/* ════════════════════════════════════════════════════════════════
                MODAL 3: Withdrawal
            ════════════════════════════════════════════════════════════════ */}
            <Modal open={withdrawOpen} onClose={() => setWithdrawOpen(false)}>
                <Paper sx={modalPaperSx(theme)}>
                    {/* Header */}
                    <Box sx={{
                        px: 3, py: 2.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                    }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            }}>
                                <AccountBalanceWalletIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: FONT }}>
                                Withdraw Funds
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Body */}
                    <Box sx={{ px: 3, py: 3 }}>
                        {/* Available Balance - shows relevant balance based on method */}
                        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }}>
                            <Box sx={{
                                p: 2, borderRadius: 2, flex: 1,
                                bgcolor: withdrawMethod !== 'crypto' ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.divider, 0.3),
                                border: `1px solid ${withdrawMethod !== 'crypto' ? alpha(theme.palette.primary.main, 0.1) : theme.palette.divider}`,
                            }}>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: FONT, fontWeight: 500 }}>
                                    Fiat Balance
                                </Typography>
                                <Typography sx={{
                                    fontWeight: 700, fontFamily: MONO,
                                    fontFeatureSettings: '"tnum"', fontSize: '1.1rem',
                                    color: withdrawMethod !== 'crypto' ? theme.palette.primary.main : theme.palette.text.disabled,
                                }}>
                                    ${balance ? Number(balance).toFixed(2) : '0.00'}
                                </Typography>
                            </Box>
                            <Box sx={{
                                p: 2, borderRadius: 2, flex: 1,
                                bgcolor: withdrawMethod === 'crypto' ? alpha('#2775ca', 0.04) : alpha(theme.palette.divider, 0.3),
                                border: `1px solid ${withdrawMethod === 'crypto' ? alpha('#2775ca', 0.15) : theme.palette.divider}`,
                            }}>
                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontFamily: FONT, fontWeight: 500 }}>
                                    Crypto Balance
                                </Typography>
                                <Typography sx={{
                                    fontWeight: 700, fontFamily: MONO,
                                    fontFeatureSettings: '"tnum"', fontSize: '1.1rem',
                                    color: withdrawMethod === 'crypto' ? '#2775ca' : theme.palette.text.disabled,
                                }}>
                                    {cryptoBalance ? Number(cryptoBalance).toFixed(2) : '0.00'} USDC
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Withdrawal Method */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: FONT, mb: 1.5 }}>
                            Withdrawal Method
                        </Typography>
                        <RadioGroup
                            value={withdrawMethod}
                            onChange={(e) => {
                                const method = e.target.value;
                                setWithdrawMethod(method);
                                setWithdrawalAmount(method === 'crypto' ? (cryptoBalance || '0') : (balance || '0'));
                            }}
                            sx={{ mb: 2.5 }}
                        >
                            <Paper variant="outlined" sx={{
                                p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                                border: withdrawMethod === 'stripe' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                                '&:hover': { borderColor: theme.palette.primary.main },
                            }} onClick={() => { setWithdrawMethod('stripe'); setWithdrawalAmount(balance || '0'); }}>
                                <FormControlLabel
                                    value="stripe"
                                    control={<Radio size="small" />}
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <CreditCardIcon sx={{ color: '#635bff', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: FONT }}>Stripe</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                                    Withdraw to your connected Stripe account
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </Paper>
                            <Paper variant="outlined" sx={{
                                p: 1.5, mb: 1, borderRadius: 2, cursor: 'pointer',
                                border: withdrawMethod === 'paypal' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                                '&:hover': { borderColor: theme.palette.primary.main },
                            }} onClick={() => { setWithdrawMethod('paypal'); setWithdrawalAmount(balance || '0'); }}>
                                <FormControlLabel
                                    value="paypal"
                                    control={<Radio size="small" />}
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <PaymentIcon sx={{ color: '#003087', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: FONT }}>PayPal</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                                    Withdraw to your PayPal account
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </Paper>
                            <Paper variant="outlined" sx={{
                                p: 1.5, borderRadius: 2, cursor: hasCryptoBalance ? 'pointer' : 'default',
                                opacity: hasCryptoBalance ? 1 : 0.5,
                                border: withdrawMethod === 'crypto' ? `2px solid #2775ca` : `1px solid ${theme.palette.divider}`,
                                '&:hover': hasCryptoBalance ? { borderColor: '#2775ca' } : {},
                            }} onClick={() => { if (hasCryptoBalance) { setWithdrawMethod('crypto'); setWithdrawalAmount(cryptoBalance || '0'); } }}>
                                <FormControlLabel
                                    value="crypto"
                                    disabled={!hasCryptoBalance}
                                    control={<Radio size="small" />}
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <CurrencyBitcoinIcon sx={{ color: '#2775ca', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: FONT }}>
                                                    Crypto (USDC)
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: FONT }}>
                                                    {!hasCryptoBalance
                                                        ? 'No crypto balance available'
                                                        : `${Number(cryptoBalance).toFixed(2)} USDC available`}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </Paper>
                        </RadioGroup>

                        {/* PayPal Email */}
                        {withdrawMethod === 'paypal' && (
                            <TextField
                                fullWidth
                                required
                                label="PayPal Email Address"
                                type="email"
                                placeholder="your@email.com"
                                value={paypalEmail}
                                onChange={(e) => setPaypalEmail(e.target.value)}
                                sx={{
                                    mb: 2.5,
                                    '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: FONT },
                                }}
                                helperText="Enter the PayPal email to receive your payout"
                            />
                        )}

                        {/* Crypto Wallet Address */}
                        {withdrawMethod === 'crypto' && (
                            <>
                                <TextField
                                    fullWidth
                                    required
                                    label="Destination Wallet Address"
                                    placeholder="0x... or your wallet address"
                                    value={cryptoWalletAddress}
                                    onChange={(e) => setCryptoWalletAddress(e.target.value)}
                                    sx={{
                                        mb: 1,
                                        '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' },
                                    }}
                                    helperText="Enter the wallet address to receive USDC"
                                />

                            </>
                        )}

                        {/* Amount */}
                        <TextField
                            fullWidth
                            required
                            label={withdrawMethod === 'crypto' ? 'USDC Amount to Withdraw' : 'Amount to Withdraw'}
                            type="number"
                            value={withdrawalAmount}
                            onChange={(e) => setWithdrawalAmount(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ mr: 1, fontFamily: MONO, fontWeight: 600, color: theme.palette.text.secondary }}>
                                        {withdrawMethod === 'crypto' ? 'USDC' : '$'}
                                    </Typography>
                                ),
                            }}
                            error={parseFloat(withdrawalAmount) > parseFloat(activeBalance)}
                            helperText={
                                parseFloat(withdrawalAmount) > parseFloat(activeBalance)
                                    ? withdrawMethod === 'crypto'
                                        ? "Cannot withdraw more than your crypto balance."
                                        : "Cannot withdraw more than your available balance."
                                    : withdrawMethod === 'crypto' ? `Available: ${Number(cryptoBalance || 0).toFixed(2)} USDC` : ""
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2, fontFamily: MONO, fontFeatureSettings: '"tnum"',
                                },
                            }}
                        />

                        {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontFamily: FONT }}>{error}</Alert>}
                    </Box>

                    {/* Footer */}
                    <Box sx={{
                        px: 3, py: 2.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                    }}>
                        {withdrawMethod === 'stripe' && !isAccountReady ? (
                            <>
                                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontFamily: FONT }}>
                                    Your Stripe account is not ready for withdrawals. Please connect it first.
                                </Alert>
                                <Button
                                    onClick={handleConnectStripe}
                                    disabled={isProcessing}
                                    fullWidth
                                    variant="contained"
                                    startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon sx={{ fontSize: 16 }} />}
                                    sx={{ ...btnSx, bgcolor: '#635bff', '&:hover': { bgcolor: alpha('#635bff', 0.88) } }}
                                >
                                    Connect & Set Up Stripe Account
                                </Button>
                            </>
                        ) : (
                            <Stack direction="row" spacing={1.5}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setWithdrawOpen(false)}
                                    sx={{ ...btnSx, flex: 1, borderColor: theme.palette.divider, color: theme.palette.text.secondary }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleWithdraw}
                                    disabled={isProcessing || isWithdrawalAmountInvalid || isCryptoFormInvalid || isPaypalEmailInvalid}
                                    variant="contained"
                                    sx={{
                                        ...btnSx, flex: 2,
                                        ...(withdrawMethod === 'crypto' ? {
                                            bgcolor: '#2775ca',
                                            '&:hover': { bgcolor: alpha('#2775ca', 0.88) },
                                        } : {}),
                                    }}
                                >
                                    {isProcessing ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <span>
                                            Withdraw{' '}
                                            <Box component="span" sx={{ fontFamily: MONO, fontFeatureSettings: '"tnum"' }}>
                                                {withdrawMethod === 'crypto' ? `${withdrawalAmount} USDC` : `$${withdrawalAmount}`}
                                            </Box>
                                            {' '}via {withdrawMethod === 'stripe' ? 'Stripe' : withdrawMethod === 'paypal' ? 'PayPal' : 'USDC'}
                                        </span>
                                    )}
                                </Button>
                            </Stack>
                        )}
                    </Box>
                </Paper>
            </Modal>
        </Box>
    );
};

export default BalanceDisplay;
